import {
  Box,
  IconButton,
  useTheme,
  Tooltip,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ColorModeContext } from "../theme";
import {
  DarkModeOutlined as DarkModeOutlinedIcon,
  LightModeOutlined as LightModeOutlinedIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  PersonOutlined as PersonOutlinedIcon,
  TimerOutlined as TimerOutlinedIcon,
} from "@mui/icons-material";

import { useSettings } from "../context/SettingsProvider";
import { useAuth } from "../auth/AuthContext";
import { useTenant } from "../context/TenantContext";

import SettingsModal from "../components/SettingsModal";
import LogoutButton from "../auth/LogoutButton";
import MeetingModal from "../components/MeetingModal";
import SuggestionModal from "../components/SuggestionModal";
import TenantSelector from "../components/TenantSelector";
import ActionButton from "../components/ActionButtons";

import { UserRole } from "../constants/userRoles.ts";

const Topbar = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  const {
    showComplete,
    setShowComplete,
    showSomedayMaybe,
    setShowSomedayMaybe,
  } = useSettings();

  const meetingInProgress = Boolean(
    localStorage.getItem("activeMeeting")
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { user } = useAuth();
  const role = user?.role ?? null;
  const isLoggedIn = Boolean(user);

  const { tenantId, setTenantId } = useTenant();

  const isSystemAdmin = role === UserRole.SYSTEM_ADMIN;
  const isAccountManager = role === UserRole.ACCOUNT_MANAGER;

  const showTenantSelector = isSystemAdmin || isAccountManager;

  const showPrivateIcons =
    role === UserRole.SYSTEM_ADMIN ||
    role === UserRole.ACCOUNT_MANAGER ||
    role === UserRole.TENANT_ADMIN ||
    role === UserRole.TENANT_STAFF;

  const iconSx = {
    color: "var(--tenant-primary)",

    "&:hover": {
      color: "var(--tenant-secondary)",
      bgcolor: "rgba(255,255,255,0.06)",
    },
  };

  const handleCloseSnackbar = () => {
    setSnackbar((current) => ({
      ...current,
      open: false,
    }));
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      p={2}
    >
      <Box />

      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
      >
        {showTenantSelector && (
          <TenantSelector
            value={tenantId}
            onChange={setTenantId}
          />
        )}

        <Tooltip title="Toggle theme">
          <IconButton
            onClick={colorMode.toggleColorMode}
            sx={iconSx}
          >
            {theme.palette.mode === "dark" ? (
              <DarkModeOutlinedIcon />
            ) : (
              <LightModeOutlinedIcon />
            )}
          </IconButton>
        </Tooltip>

        {showPrivateIcons && (
          <>
            <Tooltip title="Suggestions">
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                }}
              >
                <ActionButton
                  tone="teal"
                  onClick={() =>
                    setShowSuggestionModal(true)
                  }
                >
                  Add Your Suggestions
                </ActionButton>
              </Box>
            </Tooltip>

            <SuggestionModal
              open={showSuggestionModal}
              onClose={() =>
                setShowSuggestionModal(false)
              }
            />

            <Tooltip title="Notifications">
              <IconButton sx={iconSx}>
                <NotificationsOutlinedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Track Meetings">
              <IconButton
                onClick={() =>
                  setShowMeetingModal(true)
                }
                sx={{
                  ...iconSx,
                  color: meetingInProgress
                    ? "red"
                    : "var(--tenant-primary)",
                }}
              >
                <TimerOutlinedIcon />
              </IconButton>
            </Tooltip>

            <MeetingModal
              open={showMeetingModal}
              onClose={() =>
                setShowMeetingModal(false)
              }
              setSnackbar={setSnackbar}
            />

            <Tooltip title="Settings">
              <IconButton
                onClick={() =>
                  setShowSettingsModal(true)
                }
                sx={iconSx}
              >
                <SettingsOutlinedIcon />
              </IconButton>
            </Tooltip>

            <SettingsModal
              open={showSettingsModal}
              onClose={() =>
                setShowSettingsModal(false)
              }
              showComplete={showComplete}
              setShowComplete={setShowComplete}
              showSomedayMaybe={showSomedayMaybe}
              setShowSomedayMaybe={setShowSomedayMaybe}
            />

            <Tooltip title="Profile">
              <IconButton sx={iconSx}>
                <PersonOutlinedIcon />
              </IconButton>
            </Tooltip>
          </>
        )}

        {isLoggedIn ? (
          <Tooltip title="Logout">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
              }}
            >
              <LogoutButton />
            </Box>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            size="large"
            sx={{
              fontWeight: "bold",
              bgcolor: "var(--tenant-secondary)",
              color: "#222",
              ml: 2,
              boxShadow: 2,
              textTransform: "uppercase",

              "&:hover": {
                bgcolor: "var(--tenant-secondary)",
                filter: "brightness(0.9)",
              },
            }}
            onClick={() => navigate("/login")}
          >
            Login
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Topbar;